/**
 * The Tools panel's "MCP" scope view (the middle segment of Session | MCP | All).
 * Lists the user's connected MCP servers with an initialized-OK badge and their
 * tools nested beneath each, plus an inline Remove and a "+ Add". Clicking a
 * server, a tool, or "+" routes to the full-page manager (`/agent/<slug>/mcp`);
 * only Remove acts in place. Rendered only for the MCP agent (Maximus) to an
 * eligible user — the caller gates.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteMcpServer, listMcpServers, type McpServerView } from '../../agents/mcpApi.ts';
import './McpServersView.css';

interface Props {
  /** Agent slug the console is for (routes to `/agent/<slug>/mcp`). */
  readonly slug: string;
}

/** How many tool names to show inline before collapsing to "+N more". */
const INLINE_TOOLS = 4;

type Health = 'ok' | 'error' | 'off' | 'unknown';

function healthOf(s: McpServerView): Health {
  if (!s.enabled) {
    return 'off';
  }
  if (s.status === 'ok') {
    return 'ok';
  }
  if (s.status === 'error') {
    return 'error';
  }
  return 'unknown';
}

const BADGE: Record<Health, { glyph: string; label: string }> = {
  ok: { glyph: '✓', label: 'Connected — initialized OK' },
  error: { glyph: '!', label: 'Connection failed' },
  off: { glyph: '–', label: 'Disabled' },
  unknown: { glyph: '?', label: 'Not tested yet' },
};

export default function McpServersView({ slug }: Props) {
  const navigate = useNavigate();
  const [servers, setServers] = useState<McpServerView[] | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void listMcpServers()
      .then((s) => live && setServers(s))
      .catch(() => live && setServers([]));
    return () => {
      live = false;
    };
  }, []);

  const manage = (): void => navigate(`/agent/${slug}/mcp`);

  const remove = async (serverId: string): Promise<void> => {
    if (confirmId !== serverId) {
      setConfirmId(serverId);
      return;
    }
    setBusyId(serverId);
    try {
      const next = await deleteMcpServer(serverId);
      setServers(next);
    } catch {
      // Leave the list as-is; the full-page manager surfaces errors in detail.
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  if (servers === null) {
    return <p className="om-mcpv__muted">Loading connected tools…</p>;
  }

  return (
    <div className="om-mcpv">
      <p className="om-mcpv__hint">
        Your connected MCP servers. Click a server, a tool, or “+” to manage.
      </p>
      {servers.length === 0 ? (
        <p className="om-mcpv__muted">No servers connected yet.</p>
      ) : (
        servers.map((s) => {
          const health = healthOf(s);
          const badge = BADGE[health];
          const tools = s.tools ?? [];
          const showTools = health === 'ok' && tools.length > 0;
          return (
            <div key={s.serverId} className="om-mcpv__srv">
              <div className="om-mcpv__row">
                <button
                  type="button"
                  className="om-mcpv__open"
                  onClick={manage}
                  title={`${s.name ?? s.serverId} — manage`}
                >
                  <span
                    className={`om-mcpv__badge om-mcpv__badge--${health}`}
                    title={badge.label}
                    aria-label={badge.label}
                  >
                    {badge.glyph}
                  </span>
                  <span className="om-mcpv__nm">{s.name ?? s.serverId}</span>
                  <span className="om-mcpv__meta">{metaFor(s, health)}</span>
                  <span className="om-mcpv__go" aria-hidden="true">
                    →
                  </span>
                </button>
                <button
                  type="button"
                  className={`om-mcpv__rm${confirmId === s.serverId ? ' is-confirm' : ''}`}
                  disabled={busyId === s.serverId}
                  onClick={() => void remove(s.serverId)}
                >
                  {busyId === s.serverId ? '…' : confirmId === s.serverId ? 'Remove?' : 'Remove'}
                </button>
              </div>
              {showTools ? (
                <div className="om-mcpv__tools">
                  {tools.slice(0, INLINE_TOOLS).map((t) => (
                    <button key={t} type="button" className="om-mcpv__tool" onClick={manage}>
                      <span className="om-mcpv__tn">{t}</span>
                      <span className="om-mcpv__tg" aria-hidden="true">
                        →
                      </span>
                    </button>
                  ))}
                  {tools.length > INLINE_TOOLS ? (
                    <button type="button" className="om-mcpv__tool om-mcpv__more" onClick={manage}>
                      +{tools.length - INLINE_TOOLS} more…
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })
      )}
      <button type="button" className="om-mcpv__add" onClick={manage}>
        + Add MCP server
      </button>
    </div>
  );
}

/** The short right-aligned meta line for a server row. */
function metaFor(s: McpServerView, health: Health): string {
  const count = s.toolCount ?? s.tools?.length;
  const toolPart = count !== undefined ? `${String(count)} tool${count === 1 ? '' : 's'}` : '';
  if (health === 'off') {
    return toolPart.length > 0 ? `off · ${toolPart}` : 'off';
  }
  if (health === 'error') {
    return 'connection failed';
  }
  if (health === 'unknown') {
    return 'not tested';
  }
  return toolPart;
}
